/* eslint-disable no-unused-vars */
/* eslint-disable indent */
import React, { useState, useEffect } from "react";
import { Card, Row, Col, message, Button } from "antd";

import { DownloadOutlined } from "@ant-design/icons";
import moment from "moment";
import {
    DropDownComponent,
    TableComponent,
    CalendarComponent,
    RefreshTimestamp
} from "../../CommonComponents";
import "../styles.scss";
import Heading from "../../../../components/Heading";
import { getHubList, getHubCPList } from "../../../../services/api/hub";
import { ReportsService } from "../../../../services/api/reports";
import { downloadIncomingShipmentControlTowerReport } from "../../../../utils/downloadUtils";
import { useNavigate } from "react-router-dom";
import { useRequestCancellation } from "../../../../hooks/useRequestCancellation";
import { SHIPMENT_STATUS_LIST, getStatusDisplayText } from "../../../../constants/shipmentStatus";

// Custom status list for Incoming Shipment Report
const INCOMING_SHIPMENT_STATUS_LIST = [
    { value: "INSCANNED", label: "Inscanned" },
    { value: "HUB-OUTSCAN", label: "Hub Outscan" },
    { value: "INSCANNED_AT_TRANSIT", label: "Inscanned at Transit" },
    { value: "BAGGED", label: "Bagged" },
    { value: "IN-BAG-INSCAN", label: "In-Bag Inscan" },
    { value: "IN-BAG-OUTSCAN", label: "In-Bag Outscan" }
];
import {
    SERVICE_TYPE_OPTIONS,
    BOOKING_TYPE_OPTIONS,
    DOCUMENT_TYPE_OPTIONS,
    REPORT_FILTER_LABELS,
    REPORT_PLACEHOLDERS
} from "../../../../constants/reportConstants";
import { useCopilotReadable } from "@copilotkit/react-core";

const IncomingShipmentReport = () => {
    const { createNewRequest, isLatestRequest } = useRequestCancellation();
    const [loading, setLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        moment().subtract(0, "days"),
        moment()
    ]);
    const [hubList, setHubList] = useState([]);
    const [selectedDestinationHubs, setSelectedDestinationHubs] = useState([]);
    const [cpList, setCpList] = useState([]);
    const [selectedOriginHub, setSelectedOriginHub] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedServiceType, setSelectedServiceType] = useState([]);
    const [selectedBookingType, setSelectedBookingType] = useState([]);
    const [selectedDocumentType, setSelectedDocumentType] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
    });
    const [reportData, setReportData] = useState([]);
    const [hubUser, setHubUser] = useState();
    const [lastsynced, setLastsynced] = useState(null);
    const [nextScheduledRefresh, setNextScheduledRefresh] = useState(null);
    const navigate = useNavigate();

    // Make report data available to CopilotKit
    useCopilotReadable({
        description: "Incoming Shipment Report data including filters, results, and analytics",
        value: {
            reportType: "Incoming Shipment Report",
            reportData: {
                totalRecords: pagination.total,
                currentPage: pagination.current,
                pageSize: pagination.pageSize,
                recordsShown: reportData.length,
                data: reportData.slice(0, 10).map(item => ({
                    awbNumber: item.awbNumber,
                    bookingDate: item.bookingDate,
                    status: item.status,
                    originHub: item.originHub,
                    destinationHub: item.destinationHub,
                    serviceType: item.serviceType,
                    bookingType: item.bookingType,
                    documentType: item.documentType
                }))
            },
            appliedFilters: {
                dateRange: dateRange ? {
                    startDate: dateRange[0]?.format("YYYY-MM-DD"),
                    endDate: dateRange[1]?.format("YYYY-MM-DD")
                } : null,
                selectedDestinationHubs: selectedDestinationHubs,
                selectedOriginHub: selectedOriginHub,
                selectedStatus: selectedStatus,
                selectedServiceType: selectedServiceType,
                selectedBookingType: selectedBookingType,
                selectedDocumentType: selectedDocumentType
            },
            availableOptions: {
                hubList: hubList.map(hub => ({ value: hub.value, label: hub.label })),
                statusOptions: INCOMING_SHIPMENT_STATUS_LIST,
                serviceTypeOptions: SERVICE_TYPE_OPTIONS,
                bookingTypeOptions: BOOKING_TYPE_OPTIONS,
                documentTypeOptions: DOCUMENT_TYPE_OPTIONS
            },
            reportMetrics: {
                loading: loading,
                downloadLoading: downloadLoading,
                userRole: hubUser,
                lastUpdated: new Date().toISOString()
            }
        }
    });

    // Fetch hub list on mount
    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                const hubs = await getHubList();
                setHubList(hubs);
            } catch {
                message.error("Failed to fetch hub list");
            } finally {
                // setLoading(false);
            }
        })();
    }, []);

    // Set default hub for HUBADMIN users
    useEffect(() => {
        const user = JSON.parse(localStorage.getItem("userinfo"));
        let userid = JSON.parse(localStorage.getItem("userId"));
        setHubUser(user?.roles?.[0]);
        const activeHub = JSON.parse(localStorage.getItem("active_hub"));
        const activeHubId = activeHub?.hubId;

        if (hubList.length > 0) {
            if (activeHubId) {
                const defaultHub = hubList.find((hub) => String(hub.value) === String(activeHubId));
                if (defaultHub) {
                    setSelectedDestinationHubs([defaultHub.value]);
                }
            } else if ((user?.roles?.[0] === "ROLE_HUBADMIN" || user?.roles?.[0] === "ROLE_HUBOPERATOR")) {
                const defaultHub = hubList.find(hub => String(hub.value) === String(userid?.userInfo?.premiseId));
                if (defaultHub) {
                    setSelectedDestinationHubs([defaultHub.value]);
                }
            }
        }
    }, [hubList]);

    // Fetch report data when filters or pagination change
    useEffect(() => {
        // Wait for hubUser to be initialized
        console.log("hubUser", hubUser);
        if (!hubUser) {
            return;
        }
        console.log("selectedDestinationHubs", selectedDestinationHubs);
        // Don't fetch if no hubs are selected

        if (selectedDestinationHubs.length === 0 && (hubUser == "ROLE_HUBADMIN" || hubUser == "ROLE_HUBOPERATOR")) {
            return;
        }

        console.log("Incoming Shipment Report - Fetching with pagination:", {
            current: pagination.current,
            pageSize: pagination.pageSize,
            selectedDestinationHubs,
            dateRange
        });

        fetchReport();
        // eslint-disable-next-line
    }, [hubUser, selectedDestinationHubs, selectedOriginHub, selectedStatus, selectedServiceType, selectedBookingType, selectedDocumentType, dateRange, pagination.current, pagination.pageSize]);

    const fetchReport = async () => {
        // Guard against invalid dateRange
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            return;
        }

        // 🔥 CREATE NEW REQUEST WITH GLOBAL HOOK
        const { signal, requestId } = createNewRequest("incoming-shipment-report");

        try {
            setLoading(true);
            setReportData([]); // Clear previous data

            const selectedHubData = hubList.filter(hub => selectedDestinationHubs?.includes(hub.value));
            const selectedOriginHubData = hubList.filter(hub => selectedOriginHub?.includes(hub.value));
            const params = {
                ...(selectedOriginHubData?.length > 0 && { originHubIdList: selectedOriginHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { destinationHubId: selectedHubData.map(hub => hub.value).join(",") }),
                ...(dateRange[0] && dateRange[1] && {
                    fromDate: dateRange[0].format("YYYY-MM-DD"),
                    toDate: dateRange[1].format("YYYY-MM-DD")
                }),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                ...(selectedServiceType?.length > 0 && { serviceType: selectedServiceType.join(",") }),
                ...(selectedBookingType?.length > 0 && { bookingType: selectedBookingType.join(",") }),
                ...(selectedDocumentType?.length > 0 && { documentType: selectedDocumentType.join(",") }),
                page: (pagination.current - 1), // Convert to 0-based
                size: pagination.pageSize
            };

            // Call the incoming shipment control tower report API with signal
            const { data, total, lastRefreshedAt, nextScheduledRefresh } = await ReportsService.getIncomingShipmentControlTowerReport(params, { signal });

            // 🔥 ONLY UPDATE IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                console.log(`🔥 Request ${requestId} completed successfully - updating state`);
                setReportData(data);
                setLastsynced(lastRefreshedAt);
                setNextScheduledRefresh(nextScheduledRefresh);
                setPagination(prev => ({
                    ...prev,
                    total,
                }));
            }

        } catch (error) {
            // 🔥 DON'T UPDATE STATE IF REQUEST WAS CANCELLED
            if (error.name === "AbortError") {
                console.log("🔥 Request was cancelled at HTTP level - no state update");
                return;
            }

            // Only update if this is still the latest request
            if (isLatestRequest(requestId)) {
                message.error("Failed to fetch report data");
                setReportData([]);
                setPagination(prev => ({
                    ...prev,
                    total: 0
                }));
            }
        } finally {
            // 🔥 ONLY UPDATE LOADING IF THIS IS THE LATEST REQUEST
            if (isLatestRequest(requestId)) {
                setLoading(false);
            }
        }
    };

    const handleTableChange = (newPagination) => {
        setPagination(prev => ({
            ...prev,
            current: newPagination.current,
            pageSize: newPagination.pageSize
        }));
    };

    const handleDownload = async () => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) {
            message.error("Please select a date range for download");
            return;
        }
        setDownloadLoading(true);
        try {
            const selectedHubData = hubList.filter(hub => selectedDestinationHubs?.includes(hub.value));
            const selectedOriginHubData = hubList.filter(hub => selectedOriginHub?.includes(hub.value));
            const downloadParams = {
                ...(selectedOriginHubData?.length > 0 && { originHubIdList: selectedOriginHubData.map(hub => hub.value).join(",") }),
                ...(selectedHubData?.length > 0 && { destinationHubId: selectedHubData.map(hub => hub.value).join(",") }),
                fromDate: dateRange[0].format("YYYY-MM-DD"),
                toDate: dateRange[1].format("YYYY-MM-DD"),
                ...(selectedStatus?.length > 0 && { currentStatus: selectedStatus.join(",") }),
                ...(selectedServiceType?.length > 0 && { serviceType: selectedServiceType.join(",") }),
                ...(selectedBookingType?.length > 0 && { bookingType: selectedBookingType.join(",") }),
                ...(selectedDocumentType?.length > 0 && { documentType: selectedDocumentType.join(",") })
            };
            await downloadIncomingShipmentControlTowerReport(downloadParams);
            message.success("Report downloaded successfully");
        } catch (error) {
            console.error("Download error:", error);
            message.error("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    // Custom handler for date range change
    const handleDateRangeChange = (value) => {
        setDateRange(value);
        // Do NOT clear reportData here!
    };

    // Table columns
    const columns = [
        {
            title: "Sr No.",
            dataIndex: "index",
            key: "index",
            width: 80,
            render: (_, __, index) => (pagination.current - 1) * pagination.pageSize + index + 1,
        },
        {
            title: "AWB Number",
            dataIndex: "trackingId",
            key: "trackingId",
            sorter: true,
            width: 140,
            render: (text, record) => (
                <span
                    style={{ color: "#1890ff", cursor: "pointer", textDecoration: "underline" }}
                    onClick={() => {
                        const url = `/shipment-tracker?awb=${text}`;
                        window.open(url, "_blank");
                    }}
                >
                    {text}
                </span>
            )
        },
        {
            title: "Operation DateTime",
            dataIndex: "bookingTime",
            key: "bookingTime",
            sorter: true,
            width: 140,
            render: (text) => text ? moment(text).format("DD-MM-YYYY HH:mm:ss") : "-"
        },
        {
            title: "Origin Hub",
            dataIndex: "originHubName",
            key: "originHubName",
            sorter: true,
            width: 160
        },
        {
            title: "Current Hub",
            dataIndex: "currentHubName",
            key: "currentHubName",
            sorter: true,
            width: 160
        },
        {
            title: "Destination Hub",
            dataIndex: "destinationHubName",
            key: "destinationHubName",
            sorter: true,
            width: 160
        },
        {
            title: "Doc Type",
            dataIndex: "docType",
            key: "docType",
            sorter: true,
            width: 100
        },
        {
            title: "Service Type",
            dataIndex: "serviceType",
            key: "serviceType",
            sorter: true,
            width: 100
        },
        {
            title: "Booking Type",
            dataIndex: "bookingType",
            key: "bookingType",
            sorter: true,
            width: 100
        },
        {
            title: "Mode",
            dataIndex: "mode",
            key: "mode",
            sorter: true,
            width: 100
        },
        {
            title: "Status",
            dataIndex: "currentStatus",
            key: "currentStatus",
            sorter: true,
            width: 100,
            render: (text) => getStatusDisplayText(text)
        }
    ];

    return (
        <div className="report-container">
            <div style={{ backgroundColor: "white", padding: "8px 16px", borderRadius: "8px" }}>
                <Heading
                    title="Incoming Shipment"
                >
                    <div style={{ paddingTop: "5px" }}>
                        <RefreshTimestamp
                            lastRefreshedAt={lastsynced}
                            nextScheduledRefresh={nextScheduledRefresh}
                        />
                    </div>
                </Heading>
            </div>
            <Card className="report-card">
                <Row gutter={[16, 16]} className="filters-section">
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Operation Time</div>
                        <CalendarComponent
                            className="custom-calendar"
                            type="range"
                            value={dateRange && dateRange[0] && dateRange[1] ? dateRange : [moment(), moment()]}
                            onChange={handleDateRangeChange}
                            format="YYYY-MM-DD"
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Origin Hub</div>
                        <DropDownComponent
                            placeholder="Origin Hub"
                            options={hubList}
                            onChange={setSelectedOriginHub}
                            value={selectedOriginHub}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Destination Hub</div>
                        <DropDownComponent
                            placeholder="Destination Hub"
                            options={hubList}
                            onChange={setSelectedDestinationHubs}
                            value={selectedDestinationHubs}
                            allowClear
                            mode={hubUser !== "ROLE_HUBADMIN" || hubUser !== "ROLE_HUBOPERATOR" ? "multiple" : "default"}
                            disabled={hubUser === "ROLE_HUBADMIN" || hubUser === "ROLE_HUBOPERATOR"}
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">Status</div>
                        <DropDownComponent
                            placeholder="Select Status"
                            options={INCOMING_SHIPMENT_STATUS_LIST}
                            onChange={setSelectedStatus}
                            value={selectedStatus}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">{REPORT_FILTER_LABELS.SERVICE_TYPE}</div>
                        <DropDownComponent
                            placeholder={REPORT_PLACEHOLDERS.SELECT_SERVICE_TYPE}
                            options={SERVICE_TYPE_OPTIONS}
                            onChange={setSelectedServiceType}
                            value={selectedServiceType}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <div className="filter-label">{REPORT_FILTER_LABELS.BOOKING_TYPE}</div>
                        <DropDownComponent
                            placeholder={REPORT_PLACEHOLDERS.SELECT_BOOKING_TYPE}
                            options={BOOKING_TYPE_OPTIONS}
                            onChange={setSelectedBookingType}
                            value={selectedBookingType}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        {/* <div className="filter-label">{REPORT_FILTER_LABELS.DOCUMENT_TYPE}</div>
                        <DropDownComponent
                            placeholder={REPORT_PLACEHOLDERS.SELECT_DOCUMENT_TYPE}
                            options={DOCUMENT_TYPE_OPTIONS}
                            onChange={setSelectedDocumentType}
                            value={selectedDocumentType}
                            allowClear
                            mode="multiple"
                            maxTagCount="responsive"
                            showSearch={true}
                            optionFilterProp="label"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                        /> */}
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6} style={{ display: "flex", alignItems: "flex-end", justifyContent: "flex-end" }}>
                        <Button
                            type="primary"
                            icon={<DownloadOutlined />}
                            loading={downloadLoading}
                            onClick={handleDownload}
                            disabled={!dateRange || !dateRange[0] || !dateRange[1]}
                        >
                            Download Report
                        </Button>
                    </Col>
                </Row>
            </Card>
            <TableComponent
                columns={columns}
                dataSource={reportData}
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                scroll={{ y: "calc(100vh - 300px)", x: "max-content" }}
                sticky
                rowKey="trackingId"
            />
        </div>
    );
};

export default IncomingShipmentReport; 
